import { motion } from "framer-motion";
import { Plug, Radar, Sparkles } from "lucide-react";

const STEPS = [
    {
        n: "01",
        icon: Plug,
        title: "Connect",
        body: "Secure OAuth link to your Salesforce Sandbox or Production in under 2 minutes. Read-only by default.",
    },
    {
        n: "02",
        icon: Radar,
        title: "Scan",
        body: "Our engine cross-references every Contact and Lead against real-time employment signals to surface stale titles and job changes.",
    },
    {
        n: "03",
        icon: Sparkles,
        title: "Clean",
        body: "Review the Ghost Leads, approve in bulk, and we write the archive back to Salesforce — your pipeline is 100% live.",
    },
];

export default function HowItWorks() {
    return (
        <section
            id="how"
            data-testid="how-section"
            className="relative py-28 lg:py-36 border-t border-white/5"
        >
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <div className="max-w-2xl">
                    <div className="text-[11px] uppercase tracking-[0.25em] font-mono text-[#10B981] font-bold">
                        How It Works
                    </div>
                    <h2 className="mt-4 font-display text-[36px] sm:text-[44px] lg:text-[56px] font-semibold leading-[1.05] tracking-[-0.025em]">
                        From dirty to{" "}
                        <span className="text-volt-gradient">
                            clean in three steps.
                        </span>
                    </h2>
                </div>

                <div className="relative mt-16">
                    {/* Connector line (desktop) */}
                    <div className="hidden md:block absolute top-[52px] left-[10%] right-[10%] h-px dashed-connector opacity-70 pointer-events-none" />

                    <div className="grid md:grid-cols-3 gap-6 relative">
                        {STEPS.map((s, i) => (
                            <motion.div
                                key={s.n}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{
                                    duration: 0.5,
                                    delay: i * 0.12,
                                }}
                                data-testid={`how-step-${i}`}
                                className="relative"
                            >
                                {/* Number node */}
                                <div className="relative mx-auto md:mx-0 h-[104px] w-[104px] flex items-center justify-center">
                                    <div
                                        className="absolute inset-0 rounded-full animate-pulse-ring"
                                        style={{
                                            background:
                                                "radial-gradient(circle, rgba(16,185,129,0.08), transparent 65%)",
                                        }}
                                    />
                                    <div className="relative h-[84px] w-[84px] rounded-full bg-[#0a0a0a] border border-[#10B981]/35 flex items-center justify-center">
                                        <s.icon
                                            size={28}
                                            strokeWidth={1.5}
                                            className="text-[#10B981]"
                                        />
                                        <span className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-[#10B981] text-black text-[11px] font-mono font-bold flex items-center justify-center">
                                            {s.n.replace("0", "")}
                                        </span>
                                    </div>
                                </div>

                                <h3 className="mt-7 font-display text-[24px] font-medium tracking-tight">
                                    {s.title}
                                </h3>
                                <p className="mt-3 max-w-sm text-[14px] leading-relaxed text-neutral-400">
                                    {s.body}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
